<template>
    <AppLayout>
        <!-- <div class="container p-4 mx-auto md:px-1 md:py-4"> -->
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Dashboard</h1>
                <p class="text-sm text-gray-500 dark:text-gray-300">Welcome back, {{ $page.props.user.name }}!</p>
            </div>

            <!-- Filter + Greeting Row -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <p class="text-lg font-medium text-gray-700 dark:text-gray-200">{{ greetings }}</p>
                <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <!-- <t-date-picker v-model="dateRange" range format="YYYY-MM-DD" class="w-full sm:w-64" /> -->
                    <t-select
                        v-if="!isRetailer"
                        v-model="filterBy"
                        :options="filterByOptions"
                        text-attribute="name"
                        value-attribute="value"
                        class="w-full sm:w-64"
                    />
                </div>
            </div>

            <!-- Dashboard Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <!-- Card Template -->
                <div
                    v-for="card in dashboardCards"
                    :key="card.key"
                    v-if="card.condition"
                    class="p-6 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition duration-300 cursor-pointer border border-gray-100 dark:border-gray-700"
                    @click="goto(card.route)"
                >
                    <div class="flex items-center space-x-4">
                        <!-- Icon -->
                        <div class="p-3 rounded-md bg-gray-100 dark:bg-gray-700">
                            <component
                                :is="card.icon"
                                class="w-8 h-8 text-green-600 dark:text-green-400"
                            />
                        </div>
                        <div>
                            <!-- Label -->
                            <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {{ card.label }}
                            </p>

                            <!-- Count -->
                            <p class="text-3xl font-bold text-gray-900 dark:text-white">
                                {{ card.count }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Recent Orders Table -->
            <div
                v-if="isAdmin || isWholesaler"
                class="mt-10"
            >
                <div class="mb-4 text-xl font-semibold lg:text-xl dark:text-white">
                    {{ $t('LBL_RECENT_ORDERS') }}
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <tbody>
                                <template v-for="(group, groupLabel) in groupedBills">
                                    <tr
                                        v-if="group.length"
                                        class="bg-gray-200 dark:bg-gray-700"
                                    >
                                        <td class="px-4 py-3 font-semibold cursor-pointer">
                                            <div
                                                class="flex justify-start items-center w-full gap-3"
                                                @click="toggleGroup(groupLabel)"
                                            >
                                                <svg
                                                    :class="{ 'rotate-90': !collapsedGroups[groupLabel] }"
                                                    class="w-4 h-4 ml-2 transition-transform duration-200 transform"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    viewBox="0 0 24 24"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path d="M9 5l7 7-7 7"></path>
                                                </svg>
                                                <span>{{ groupLabel }}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr
                                        class="bg-white rounded-xl shadow border border-gray-200"
                                        v-if="group.length && !collapsedGroups[groupLabel]"
                                        v-for="bill in group"
                                        :key="bill.id"
                                        :class="[
                                            'border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700',
                                            bill.delivery_status === 'Placed'
                                                ? 'bg-red-50 dark:bg-red-900/30'
                                                : bill.delivery_status === 'Packed'
                                                ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                : bill.delivery_status === 'Shipped'
                                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                                : bill.delivery_status === 'Delivered'
                                                ? 'bg-green-50 dark:bg-green-900/30'
                                                : '',
                                        ]"
                                    >
                                        <td class="p-4 w-full">
                                            <!-- GRID layout for alignment -->
                                            <div
                                                class="grid grid-cols-1 md:grid-cols-[30%_10%_20%_10%_20%] gap-4 items-start"
                                            >
                                                <!-- Order Info -->
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">
                                                        {{ isAdmin ? 'Order Of' : 'Order Placed' }}
                                                    </p>
                                                    <p class="font-medium text-gray-800">
                                                        {{ isAdmin ? bill.wholesaler.name : bill.user.name }}
                                                    </p>
                                                    <p class="text-sm text-gray-500">
                                                        {{ bill.created_at | dateLabel }}
                                                    </p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">Total</p>
                                                    <p class="font-medium text-green-600">
                                                        {{ config(common.currency) }}
                                                        {{ sumAmountFromTransactions(bill.transactions) }}
                                                    </p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">{{ bill.delivery_status }} Date</p>
                                                    <p class="font-medium text-gray-800">{{ bill.updated_at | LLL }}</p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">Status</p>
                                                    <span
                                                        :class="
                                                            bill.payment_status === 'Paid'
                                                                ? 'bg-green-400'
                                                                : 'bg-yellow-400'
                                                        "
                                                        class="inline-block px-2 py-1 text-xs font-semibold text-white rounded"
                                                        >{{ bill.payment_status }}</span
                                                    >
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">
                                                        Order #
                                                        <span class="font-medium text-gray-800">{{
                                                            bill.bill_number
                                                        }}</span>
                                                    </p>

                                                    <!-- Action Links -->
                                                    <div
                                                        class="flex flex-wrap items-center justify-between gap-4 mt-1 space-x-2"
                                                    >
                                                        <t-button
                                                            variant="text"
                                                            class="text-sm text-blue-600"
                                                            @click="goto('account.show', { orderId: bill.bill_number })"
                                                        >
                                                            View Order Detail
                                                        </t-button>
                                                        <!-- In recent order, there will be no invoice to see -->
                                                        <!-- <t-button
                                                            variant="text"
                                                            class="text-sm text-blue-600"
                                                            @click="printInvoice(bill)"
                                                        >
                                                            View Invoice
                                                        </t-button> -->
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!allDetails.bills.length"
                                    class="text-center"
                                >
                                    <td class="px-4 py-4 text-gray-500 dark:text-gray-400">
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <!-- <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th
                                        v-if="isAdmin"
                                        class="px-4 py-3"
                                    >
                                        WHOLESALER
                                    </th>
                                    <th
                                        v-if="isWholesaler"
                                        class="px-4 py-3"
                                    >
                                        RETAILER
                                    </th>
                                    <th class="px-4 py-3">ORDER NUMBER</th>
                                    <th class="px-4 py-3">PRODUCT COUNT</th>
                                    <th class="px-4 py-3">AMOUNT</th>
                                    <th class="px-4 py-3">PAYMENT</th>
                                    <th class="px-4 py-3">ORDER</th>
                                    <th class="px-4 py-3">ORDER PLACED ON</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(group, groupLabel) in groupedBills">
                                    <tr
                                        v-if="group.length"
                                        class="bg-gray-200 dark:bg-gray-700"
                                    >
                                        <td
                                            colspan="7"
                                            class="px-4 py-3 font-semibold cursor-pointer"
                                        >
                                            <div
                                                class="flex justify-start items-center w-full gap-3"
                                                @click="toggleGroup(groupLabel)"
                                            >
                                                <svg
                                                    :class="{ 'rotate-90': !collapsedGroups[groupLabel] }"
                                                    class="w-4 h-4 ml-2 transition-transform duration-200 transform"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    viewBox="0 0 24 24"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path d="M9 5l7 7-7 7"></path>
                                                </svg>
                                                <span>{{ groupLabel }}</span>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr
                                        v-if="group.length && !collapsedGroups[groupLabel]"
                                        v-for="bill in group"
                                        :key="bill.id"
                                        :class="[
                                            'border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
                                            bill.delivery_status === 'Placed'
                                                ? 'bg-red-50 dark:bg-red-900/30'
                                                : bill.delivery_status === 'Packed'
                                                ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                : bill.delivery_status === 'Shipped'
                                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                                : bill.delivery_status === 'Delivered'
                                                ? 'bg-green-50 dark:bg-green-900/30'
                                                : '',
                                        ]"
                                        @click="goto('account.show', { orderId: bill.bill_number })"
                                    >
                                        <td
                                            v-if="isAdmin"
                                            class="px-4 py-3"
                                        >
                                            {{ bill.wholesaler?.name }}
                                        </td>
                                        <td
                                            v-if="isWholesaler"
                                            class="px-4 py-3"
                                        >
                                            {{ bill.user?.name }}
                                        </td>
                                        <td class="px-4 py-3">{{ bill.bill_number }}</td>
                                        <td class="px-4 py-3">{{ bill.transactions.length }}</td>
                                        <td class="px-4 py-3 font-semibold text-green-600 dark:text-green-400">
                                            {{ config(common.currency) }}
                                            {{ sumAmountFromTransactions(bill.transactions) }}
                                        </td>
                                        <td class="px-4 py-3">
                                            <div
                                                :class="
                                                    bill.payment_status === 'Paid' ? 'bg-green-400' : 'bg-yellow-400'
                                                "
                                                class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                            >
                                                {{ bill.payment_status }}
                                            </div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <span
                                                :class="{
                                                    'bg-red-400': bill.delivery_status === 'Placed',
                                                    'bg-yellow-400': bill.delivery_status === 'Packed',
                                                    'bg-blue-400': bill.delivery_status === 'Shipped',
                                                    'bg-green-400': bill.delivery_status === 'Delivered',
                                                }"
                                                class="text-white px-2 py-1 rounded text-xs"
                                            >
                                                {{ bill.delivery_status }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3">{{ bill.created_at | dateLabel }}</td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!allDetails.bills.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="7"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table> -->
                    </div>
                    <!-- <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p> -->
                </div>
            </div>

            <!-- <div v-if="isWholesaler" class="mt-10">
                <div class="mb-4 text-xl font-semibold lg:text-xl dark:text-white">
                    {{ $t('TXT_RECENT_ORDER') }}
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm text-left text-gray-700 dark:text-gray-300">
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">Retailer Name</th>
                                    <th class="px-4 py-3">Order Number</th>
                                    <th class="px-4 py-3">Product Sold</th>
                                    <th class="px-4 py-3">Amount</th>
                                    <th class="px-4 py-3">Payment Status</th>
                                    <th class="px-4 py-3">Order Status</th>
                                    <th class="px-4 py-3">Order Placed On</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(group, groupLabel) in groupedBills">
                                    <tr v-if="group.length" class="bg-gray-200 dark:bg-gray-700">
                                        <td colspan="7" class="px-4 py-3 font-semibold cursor-pointer">
                                            <div class="flex justify-between items-center w-full"
                                                @click="toggleGroup(groupLabel)">
                                                <span>{{ groupLabel }}</span>
                                                <svg :class="{ 'rotate-90': !collapsedGroups[groupLabel] }"
                                                    class="w-4 h-4 ml-2 transition-transform duration-200 transform"
                                                    fill="none" stroke="currentColor" stroke-width="2"
                                                    viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
                                                    <path d="M9 5l7 7-7 7"></path>
                                                </svg>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr v-if="group.length && !collapsedGroups[groupLabel]" v-for="bill in group"
                                        :key="bill.id" :class="[
                                            'border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
                                            bill.delivery_status === 'Placed' ? 'bg-red-50 dark:bg-red-900/30' :
                                                bill.delivery_status === 'Packed' ? 'bg-yellow-50 dark:bg-yellow-900/30' :
                                                    bill.delivery_status === 'Shipped' ? 'bg-blue-50 dark:bg-blue-900/30' :
                                                        bill.delivery_status === 'Delivered' ? 'bg-green-50 dark:bg-green-900/30' : ''
                                        ]" @click="goto('account.show', { 'orderId': bill.bill_number })">
                                        <td class="px-4 py-3">{{ bill.user.name }}</td>
                                        <td class="px-4 py-3">{{ bill.bill_number }}</td>
                                        <td class="px-4 py-3">{{ bill.transactions.length }}</td>
                                        <td class="px-4 py-3 font-semibold text-green-600 dark:text-green-400">
                                            {{ config(common.currency) }} {{
                                                sumAmountFromTransactions(bill.transactions) }}
                                        </td>
                                        <td class="px-4 py-3">{{ bill.payment_status }}</td>
                                        <td class="px-4 py-3">
                                            <span :class="{
                                                'bg-red-400': bill.delivery_status === 'Placed',
                                                'bg-yellow-400': bill.delivery_status === 'Packed',
                                                'bg-blue-400': bill.delivery_status === 'Shipped',
                                                'bg-green-400': bill.delivery_status === 'Delivered'
                                            }" class="text-white px-2 py-1 rounded text-xs">
                                                {{ bill.delivery_status }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3">{{ bill.created_at | fromNow }}</td>
                                    </tr>
                                </template>

                                <tr v-if="!allDetails.bills.length" class="text-center">
                                    <td colspan="5" class="px-4 py-4 text-gray-500 dark:text-gray-400">
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div> -->
        </div>
    </AppLayout>
</template>

<script>
    import moment from 'moment';
    import AdminIcon from '@/Components/Icons/AdminIcon.vue';
    import WholesalerIcon from '@/Components/Icons/WholesalerIcon.vue';
    import RetailerIcon from '@/Components/Icons/RetailerIcon.vue';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import ProductIcon from '@/Components/Icons/ProductIcon.vue';
    import CategoryIcon from '@/Components/Icons/CategoryIcon.vue';
    import BrandIcon from '@/Components/Icons/BrandIcon.vue';
    import common from '@/common';
    import PeopleIcon from '@/Components/Icons/PeopleIcon.vue';
    import ClockIcon from '@/Components/Icons/ClockIcon.vue';
    import CashIcon from '@/Components/Icons/CashIcon.vue';

    export default {
        name: 'AccountInformation',

        components: {
            AppLayout,
            ProductIcon,
            CategoryIcon,
            BrandIcon,
            PeopleIcon,
            ClockIcon,
            AdminIcon,
            WholesalerIcon,
            RetailerIcon,
            CashIcon,
        },

        props: {
            allDetails: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                common,
                filterBy: 'allTime',
                filterByOptions: [
                    { name: 'All Time', value: 'allTime' },
                    { name: 'Current Week', value: 'currentWeek' },
                    { name: 'Current Month', value: 'currentMonth' },
                    { name: 'Current Year', value: 'currentYear' },
                    { name: 'Last Year', value: 'lastYear' },
                ],
                orderStatusFilter: '',
                orderStatusOptions: [
                    { label: 'All', value: '' },
                    { label: 'Placed', value: 'Placed' },
                    { label: 'Packed', value: 'Packed' },
                    { label: 'Shipped', value: 'Shipped' },
                    { label: 'Delivered', value: 'Delivered' },
                ],
                collapsedGroups: {
                    Today: false,
                    Yesterday: false,
                    'This Week': false,
                    'Last Week': false,
                    Past: false,
                },
                dateRange: [],
                mounted: false,
            };
        },

        computed: {
            greetings() {
                const hours = new Date().getHours();
                if (hours < 12) return 'Good Morning';
                if (hours < 17) return 'Good Afternoon';
                return 'Good Evening';
            },

            dashboardCards() {
                const safe = (getter, fallback = 0) => {
                    try {
                        return getter() ?? fallback;
                    } catch {
                        return fallback;
                    }
                };
                return [
                    {
                        key: 'kyc',
                        icon: 'AdminIcon', // use component or SVG name
                        label: this.$t('TXT_PENDING_KYC'),
                        count: safe(() => this.allDetails.userCount.pendingKycCount),
                        condition: this.isAdmin,
                        route: 'kyc.index',
                    },
                    {
                        key: 'wholesalers',
                        icon: 'WholesalerIcon', // use component or SVG name
                        label: this.$tc('TXT_WHOLESALER', 2),
                        count: safe(() => this.allDetails.userCount.wholesalerCount),
                        condition: this.isAdmin,
                        route: 'users.management.index',
                    },
                    {
                        key: 'retailers',
                        icon: 'RetailerIcon', // use component or SVG name
                        label: this.$tc('TXT_RETAILER', 2),
                        count: safe(() => this.allDetails.userCount.retailerCount),
                        condition: this.isAdmin,
                        route: 'users.management.retailer.index',
                    },
                    {
                        key: 'products',
                        icon: 'ProductIcon', // use component or SVG name
                        label: this.$tc('TXT_PRODUCT', 2),
                        count: safe(() => this.allDetails.productCount),
                        condition: !this.isRetailer,
                        route: 'products.index',
                    },
                    {
                        key: 'categories',
                        icon: 'CategoryIcon', // use component or SVG name
                        label: this.$tc('TXT_CATEGORY', 2),
                        count: safe(() => this.allDetails.categoryCount),
                        condition: !this.isRetailer,
                        route: 'categories.index',
                    },
                    {
                        key: 'brands',
                        icon: 'BrandIcon', // use component or SVG name
                        label: this.$tc('TXT_BRAND', 2),
                        count: safe(() => this.allDetails.brandCount),
                        condition: this.isWholesaler,
                        route: 'brands.index',
                    },
                    {
                        key: 'sales',
                        icon: 'PeopleIcon', // use component or SVG name
                        label: this.$tc('TXT_SALESPERSON', 2),
                        count: safe(() => this.allDetails.salesPersonCount),
                        condition: this.isAdmin,
                        route: '',
                    },
                    {
                        key: 'credit',
                        icon: 'CashIcon', // use component or SVG name
                        label: this.$t('TXT_PENDING_CREDIT_REQUEST'),
                        count: safe(() => this.allDetails.creditRequestCount),
                        condition: !this.isRetailer,
                        route: 'creditRequest.index',
                    },
                    {
                        key: 'complains',
                        icon: 'ClockIcon', // use component or SVG name
                        label: this.$tc('TXT_COMPLAIN', 2),
                        count: safe(() => this.allDetails.complainCount),
                        condition: this.isAdmin,
                        route: 'complains.index',
                    },
                ];
            },

            groupedBills() {
                const bills = this.allDetails?.bills || [];
                const groups = {
                    Today: [],
                    Yesterday: [],
                    'This Week': [],
                    'Last Week': [],
                    Past: [],
                };

                const today = moment();
                const yesterday = moment().subtract(1, 'day');
                const startOfWeek = moment().startOf('week');
                const startOfLastWeek = moment().subtract(1, 'week').startOf('week');
                const endOfLastWeek = moment(startOfWeek).subtract(1, 'day');

                for (const order of bills || []) {
                    const billDate = moment(order.created_at);

                    if (billDate.isSame(today, 'day')) {
                        groups['Today'].push(order);
                    } else if (billDate.isSame(yesterday, 'day')) {
                        groups['Yesterday'].push(order);
                    } else if (billDate.isSameOrAfter(startOfWeek)) {
                        groups['This Week'].push(order);
                    } else if (billDate.isBetween(startOfLastWeek, endOfLastWeek, 'day', '[]')) {
                        groups['Last Week'].push(order);
                    } else {
                        groups['Past'].push(order);
                    }
                }

                return groups;
            },
        },

        watch: {
            dateRange: {
                handler(newVal) {
                    if (newVal.length === 2 && this.mounted) {
                        this.$inertia.get(
                            route('dashboard', {
                                from: this.dateRange[0],
                                to: this.dateRange[1],
                                filterBy: this.filterBy,
                            }),
                        );
                    }
                },
            },

            filterBy: {
                handler() {
                    if (this.mounted) {
                        this.$inertia.get(
                            route('dashboard', {
                                from: this.dateRange[0],
                                to: this.dateRange[1],
                                filterBy: this.filterBy,
                            }),
                        );
                    }
                },
            },
        },

        mounted() {
            if (this.allDetails.filtersApplied?.from && this.allDetails.filtersApplied?.to) {
                this.dateRange = [this.allDetails.filtersApplied.from, this.allDetails.filtersApplied.to];
            }
            if (this.allDetails.filtersApplied?.filterBy) {
                this.filterBy = this.allDetails.filtersApplied.filterBy;
            }
            // Prevent watchers from firing immediately
            setTimeout(() => {
                this.mounted = true;
            }, 1000);
        },

        methods: {
            sumAmountFromTransactions(transactions = []) {
                return transactions.reduce((total, tx) => total + parseFloat(tx.grand_total || 0), 0).toFixed(2);
            },

            toggleGroup(groupLabel) {
                this.$set(this.collapsedGroups, groupLabel, !this.collapsedGroups[groupLabel]);
            },

            printInvoice(bill) {
                this.newTab('invoice.print', { billId: bill.id });
            },
        },
    };
</script>
